import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Upload,
  Settings,
  MapPin,
  Route,
  Ruler,
  Hand,
  Trash2,
  Save,
  Loader2,
  Scroll,
  Lock,
  Sparkles,
  Timer,
  BookMarked,
  History,
  Download,
  Eye,
  EyeOff,
  Pencil,
} from "lucide-react";
import Button from "../../components/ui/Button";
import { useAuth } from "../../contexts/AuthContext";
import { cartomancerApi, mapFileUrl } from "./api";
import {
  CartomancerLoading,
  CartomancerAccessDenied,
  CartomancerHeader,
} from "./CartomancerShell";
import MapCanvas from "./MapCanvas";
import {
  LocationDialog,
  LocationInfoPanel,
  PremiumLockPopup,
} from "./dialogs";
import { CategoryIcon } from "./PinIcon";
import FantasyClock from "./FantasyClock";
import PathManager, {
  buildInitialLegs,
  eventDurationDays,
} from "./PathManager";
import TravelSummary from "./TravelSummary";
import SettingsPanel from "./SettingsPanel";
import TutorialOverlay from "./TutorialOverlay";
import {
  TimelinePanel,
  JournalPanel,
  WikiPanel,
} from "./PremiumPanels";
import useAppProtection from "./useAppProtection";
import useCloudSync from "./useCloudSync";
import CloudSyncBadge from "./CloudSyncBadge";
import {
  formatDurationDays,
  formatDistance,
} from "./formatDuration";
import { X as XIcon } from "lucide-react";

// Module-level flag that survives React StrictMode double-mount in dev.
// The sessionStorage flag is only set once the splash animation completes.
let _splashShownThisSession = false;

export default function EditorPage() {
  const { projectId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [state, setState] = useState({
    loading: true,
    error: null,
    denied: false,
  });

  const [project, setProject] = useState(null);
  const [map, setMap] = useState(null);
  const [locations, setLocations] = useState([]);
  const [paths, setPaths] = useState([]);
  const [logs, setLogs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [plan, setPlan] = useState("basic");

  const [uploading, setUploading] = useState(false);
  const [tool, setTool] = useState("view");
  const [draftPath, setDraftPath] = useState(null);
  const [legs, setLegs] = useState([]);
  const [legClipboard, setLegClipboard] = useState(null);
  const [rulerPoints, setRulerPoints] = useState([]);

  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [infoLocation, setInfoLocation] = useState(null);
  const [editingLoc, setEditingLoc] = useState(null);

  const [showSummary, setShowSummary] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showPremiumLock, setShowPremiumLock] = useState(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [showJournal, setShowJournal] = useState(false);
  const [showWiki, setShowWiki] = useState(false);
  const [confirmDeleteMap, setConfirmDeleteMap] = useState(false);
  const [focusTarget, setFocusTarget] = useState(null);
  const [showClockEdit, setShowClockEdit] = useState(false);

  const [imgSize, setImgSize] = useState({
    w: 1000,
    h: 1000,
  });

  const [visiblePathIds, setVisiblePathIds] = useState(new Set());
  const [recalledOriginal, setRecalledOriginal] = useState(null);

  const tutorialShownRef = useRef(false);

  const [sidebarTab, setSidebarTab] = useState("places");
  const [saveStatus, setSaveStatus] = useState("");

  const fileInputRef = useRef(null);
  const snapshotTimer = useRef(null);

  const loadEverything = useCallback(async () => {
    try {
      const [projRes, accessRes, settingsRes] = await Promise.all([
        cartomancerApi.getProject(projectId),
        cartomancerApi.getAccess(),
        cartomancerApi.getSettings(),
      ]);

      const p = projRes.project;

      setProject(p);
      setMap(p.map || null);
      setPlan(accessRes.plan || "basic");
      setSettings(settingsRes.settings || {});

      if (p.map) {
        const [locsRes, pathsRes, logsRes] = await Promise.all([
          cartomancerApi.listLocations(p.map.id),
          cartomancerApi.listPaths(p.map.id),
          cartomancerApi
            .listLogs(p.id)
            .catch(() => ({ logs: [] })),
        ]);

        setLocations(locsRes.locations || []);
        setPaths(pathsRes.paths || []);
        setLogs(logsRes.logs || []);
      }

      setState({
        loading: false,
        error: null,
        denied: false,
      });
    } catch (err) {
      if (err.status === 403) {
        setState({
          loading: false,
          error: err.message,
          denied: true,
        });
      } else if (err.status === 404) {
        setState({
          loading: false,
          error: "Progetto non trovato",
          denied: false,
        });
      } else {
        setState({
          loading: false,
          error: err.message,
          denied: false,
        });
      }
    }
  }, [projectId]);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate("/login");
      return;
    }

    loadEverything();
  }, [
    user,
    authLoading,
    projectId,
    loadEverything,
    navigate,
  ]);

  // Apply screenshot / print protection
  useAppProtection(settings?.screenshot_protection !== false);

  // Apply UI scale by changing html root font-size
  useEffect(() => {
    if (!settings) return;

    const uiScale = settings.ui_scale_rem || 1;
    const textScale = settings.text_size_rem || 1;

    // Combine both scales
    const effective = uiScale * textScale;

    document.documentElement.style.fontSize = `${effective * 16}px`;

    return () => {
      document.documentElement.style.fontSize = "";
    };
  }, [
    settings?.ui_scale_rem,
    settings?.text_size_rem,
  ]);

  // Show tutorial on first load if enabled
  useEffect(() => {
    if (tutorialShownRef.current) return;

    if (
      settings &&
      settings.show_tutorial !== false &&
      project &&
      map
    ) {
      tutorialShownRef.current = true;
      setShowTutorial(true);
    }
  }, [
    settings?.show_tutorial,
    project?.id,
    !!map,
  ]);

  // Snapshot autosave
  const scheduleSnapshot = useCallback(() => {
    if (!project) return;

    if (snapshotTimer.current) {
      clearTimeout(snapshotTimer.current);
    }

    setSaveStatus("saving");

    snapshotTimer.current = setTimeout(async () => {
      try {
        await cartomancerApi.saveSnapshot(project.id, {
          map,
          locations,
          paths,
          updated_at: new Date().toISOString(),
        });

        setSaveStatus("saved");

        setTimeout(() => {
          setSaveStatus("");
        }, 1500);
      } catch {
        setSaveStatus("error");
      }
    }, 800);
  }, [project, map, locations, paths]);

  useEffect(() => {
    if (project) {
      scheduleSnapshot();
    }
  }, [
    locations,
    paths,
    map?.scale_pixels,
    map?.scale_distance,
    map?.scale_unit,
    map?.ig_day,
    map?.ig_month,
    map?.ig_year,
    map?.ig_hour,
  ]);

  const handleUpload = async (file) => {
    if (!file) return;

    setUploading(true);

    try {
      const res = await cartomancerApi.uploadMap(
        projectId,
        file
      );

      setMap(res.map);
      setLocations([]);
      setPaths([]);
    } catch (err) {
      alert(err.message);
    } finally {
      setUploading(false);
    }
  };

  const startPath = () => {
    setTool("path");
    setDraftPath({
      points: [],
    });
    setLegs([]);
    setInfoLocation(null);
  };

  const handleCanvasClick = (fx, fy) => {
    if (!map) return;

    if (tool === "pin") {
      setEditingLoc({
        map_id: map.id,
        x: fx,
        y: fy,
        name: "",
        category: "city",
        description: "",
        npcs: "",
        treasures: "",
        hooks: "",
        dm_notes: "",
      });
    } else if (tool === "path") {
      setDraftPath((d) => {
        const oldPoints = d?.points || [];

        const points = [
          ...oldPoints,
          {
            x: fx,
            y: fy,
          },
        ];

        // Sync legs based on new points count
        const legsNeeded = Math.max(
          0,
          points.length - 1
        );

        setLegs((prev) => {
          if (prev.length >= legsNeeded) {
            return prev;
          }

          const modeId =
            (map.travel_modes || [])[0]?.id || "walk";

          const additions = Array.from(
            {
              length: legsNeeded - prev.length,
            },
            () => ({
              environment: "wilderness",
              terrain: "plain",
              weather: "clear",
              marineCondition: "calm",
              transportId: modeId,
              event: null,
            })
          );

          return [...prev, ...additions];
        });

        return {
          points,
        };
      });
    } else if (tool === "ruler") {
      setRulerPoints((r) =>
        r.length >= 2
          ? [{ x: fx, y: fy }]
          : [
              ...r,
              {
                x: fx,
                y: fy,
              },
            ]
      );
    }
  };

  const cancelDraftPath = () => {
    setDraftPath(null);
    setLegs([]);
    setTool("view");
    setRecalledOriginal(null);
  };

  const removeLastPoint = () => {
    setDraftPath((d) => ({
      points: (d?.points || []).slice(0, -1),
    }));

    setLegs((prev) => prev.slice(0, -1));
  };

  const handlePathPointDrag = (idx, fx, fy) => {
    setDraftPath((d) => {
      const pts = [...(d?.points || [])];

      pts[idx] = {
        x: fx,
        y: fy,
      };

      return {
        points: pts,
      };
    });
  };

  const handleLegChange = (i, patch) => {
    setLegs((prev) =>
      prev.map((l, idx) =>
        idx === i
          ? {
              ...l,
              ...patch,
            }
          : l
      )
    );
  };

  const copyLeg = (i) => {
    setLegClipboard({
      ...legs[i],
    });
  };

  const pasteLeg = (i) => {
    if (!legClipboard) return;

    setLegs((prev) =>
      prev.map((l, idx) =>
        idx === i
          ? {
              ...legClipboard,
            }
          : l
      )
    );
  };

  const deleteLeg = (i) => {
    setLegs((prev) =>
      prev.filter((_, idx) => idx !== i)
    );

    setDraftPath((d) => {
      const pts = [...(d?.points || [])];

      pts.splice(i + 1, 1);

      return {
        points: pts,
      };
    });
  };

  const openSummary = () => {
    if (!draftPath || draftPath.points.length < 2) {
      return;
    }

    setShowSummary(true);
  };

  const savePathToDb = async (
    payload,
    andAdvanceTime = false
  ) => {
    try {
      // Flatten legs' embedded events into a legacy events array
      const flatEvents = legs.map(
        (l) =>
          l &&
          l.event &&
          l.event.type
            ? l.event
            : null
      );

      const created =
        await cartomancerApi.createPath({
          map_id: map.id,
          name: payload.name,
          notes: payload.description || "",
          points: draftPath.points,

          segments: legs.map((l) => ({
            terrain: l.terrain,
            weather: l.weather,
            travelModeId: l.transportId,
          })),

          legs,
          events: flatEvents,

          total_distance: payload.totalDistance,
          total_duration_days: payload.totalDays,

          color:
            settings?.path_color || "#d4af37",

          ig_day: map.ig_day,
          ig_month: map.ig_month,
          ig_year: map.ig_year,
          ig_hour: map.ig_hour,
        });

      setPaths((p) => [created, ...p]);

      // Auto-show newly saved path
      setVisiblePathIds((prev) => {
        const next = new Set(prev);

        next.add(created.id);

        return next;
      });

      // Log entry
      try {
        const logRes =
          await cartomancerApi.createLog({
            project_id: project.id,

            title: payload.name,

            date_str: `Giorno ${map.ig_day} · ${
              (map.months || [])[
                (map.ig_month || 1) - 1
              ] || ""
            } · Anno ${map.ig_year}`,

            from_place:
              payload.fromPlace || "",

            to_place:
              payload.toPlace || "",

            distance:
              payload.totalDistance,

            unit: map.scale_unit,

            duration_days:
              payload.totalDays,

            transports:
              payload.transports || [],

            events: flatEvents
              .map((ev, i) =>
                ev
                  ? {
                      index: i,
                      ...ev,
                    }
                  : null
              )
              .filter(Boolean),

            notes:
              payload.description || "",

            path_summary: {
              legs,
              points: draftPath.points,
              unit: map.scale_unit,
              startDate: payload.startDate,
              endDate: payload.endDate,
            },
          });

        if (logRes?.log) {
          setLogs((prev) => [
            logRes.log,
            ...prev,
          ]);
        }
      } catch {}

      if (andAdvanceTime) {
        await advanceDays(payload.totalDays);
      }

      setShowSummary(false);
      setRecalledOriginal(null);

      cancelDraftPath();
    } catch (err) {
      alert(err.message);
    }
  };

  const advanceDays = async (days) => {
    try {
      const res =
        await cartomancerApi.advanceMap(
          map.id,
          days
        );

      setMap((m) => ({
        ...m,
        ...res,
      }));
    } catch (err) {
      alert(err.message);
    }
  };

  const handleShortRest = () =>
    advanceDays(1 / 24);

  const handleLongRest = () =>
    advanceDays(8 / 24);

  const saveManualDate = async ({
    ig_day,
    ig_month,
    ig_year,
    ig_hour,
  }) => {
    try {
      const isNight =
        ig_hour >= 18 || ig_hour < 6;

      await cartomancerApi.updateDate(
        map.id,
        {
          ig_day,
          ig_month,
          ig_year,
          ig_hour,
          is_night: isNight,
        }
      );

      setMap((m) => ({
        ...m,
        ig_day,
        ig_month,
        ig_year,
        ig_hour,
        is_night: isNight,
      }));

      setShowClockEdit(false);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleSaveSettings = async (
    newSettings
  ) => {
    try {
      const res =
        await cartomancerApi.updateSettings(
          newSettings
        );

      setSettings(res.settings);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleSaveMap = async (newMap) => {
    try {
      const res =
        await cartomancerApi.updateScale(
          map.id,
          {
            scale_pixels:
              newMap.scale_pixels,
            scale_distance:
              newMap.scale_distance,
            scale_unit:
              newMap.scale_unit,
            travel_modes:
              newMap.travel_modes,
            months:
              newMap.months,
          }
        );

      if (res?.map) {
        setMap(res.map);
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const handleSaveLocation = async (
    form
  ) => {
    try {
      if (form.id) {
        await cartomancerApi.updateLocation(
          form.id,
          form
        );

        setLocations((ls) =>
          ls.map((l) =>
            l.id === form.id
              ? {
                  ...l,
                  ...form,
                }
              : l
          )
        );

        setInfoLocation((prev) =>
          prev && prev.id === form.id
            ? {
                ...prev,
                ...form,
              }
            : prev
        );
      } else {
        const created =
          await cartomancerApi.createLocation({
            map_id: map.id,
            name: form.name || "Senza nome",
            x: form.x,
            y: form.y,
            category: form.category,
            description: form.description,
            npcs: form.npcs,
            treasures: form.treasures,
            hooks: form.hooks,
            dm_notes: form.dm_notes,
          });

        setLocations((ls) => [
          ...ls,
          created,
        ]);
      }

      setEditingLoc(null);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDeleteLocation = async (
    loc
  ) => {
    if (!loc.id) {
      return setEditingLoc(null);
    }

    try {
      await cartomancerApi.deleteLocation(
        loc.id
      );

      setLocations((ls) =>
        ls.filter((l) => l.id !== loc.id)
      );

      setEditingLoc(null);
      setInfoLocation(null);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleLocationDrag = (
    locId,
    fx,
    fy
  ) => {
    setLocations((ls) =>
      ls.map((l) =>
        l.id === locId
          ? {
              ...l,
              x: fx,
              y: fy,
            }
          : l
      )
    );

    if (snapshotTimer.current) {
      clearTimeout(snapshotTimer.current);
    }

    snapshotTimer.current = setTimeout(
      async () => {
        try {
          await cartomancerApi.updateLocation(
            locId,
            {
              x: fx,
              y: fy,
            }
          );
        } catch {}
      },
      400
    );
  };

  const handleDeletePath = async (id) => {
    try {
      await cartomancerApi.deletePath(id);

      setPaths((ps) =>
        ps.filter((p) => p.id !== id)
      );
    } catch (err) {
      alert(err.message);
    }
  };

  const recallSavedPath = async (path) => {
    // Snapshot original so cancel can restore
    setRecalledOriginal(path);

    const points = (
      path.points || []
    ).map((p) => ({
      x: p.x,
      y: p.y,
    }));

    const rawLegs =
      path.legs &&
      path.legs.length > 0
        ? path.legs.map((l) => ({
            ...l,
          }))
        : (path.segments || []).map(
            (s) => ({
              environment: "wilderness",
              terrain:
                s.terrain || "plain",
              weather:
                s.weather || "clear",
              marineCondition: "calm",
              transportId:
                s.travelModeId ||
                (
                  map.travel_modes || []
                )[0]?.id ||
                "walk",
            })
          );

    // Migrate legacy events array
    const legacyEvents =
      path.events || [];

    const migratedLegs = rawLegs.map(
      (l, idx) => {
        if (l.event !== undefined) {
          return {
            marineCondition:
              l.marineCondition ||
              "calm",
            event: l.event,
            ...l,
          };
        }

        const ev =
          legacyEvents[idx];

        return {
          environment:
            l.environment ||
            "wilderness",

          terrain:
            l.terrain || "plain",

          weather:
            l.weather || "clear",

          marineCondition:
            l.marineCondition ||
            "calm",

          transportId:
            l.transportId ||
            (
              map.travel_modes || []
            )[0]?.id ||
            "walk",

          event:
            ev && ev.type
              ? {
                  ...ev,
                }
              : null,
        };
      }
    );

    setDraftPath({
      points,
    });

    setLegs(migratedLegs);
    setTool("path");

    // Hide original saved path while editing
    setVisiblePathIds((prev) => {
      const next = new Set(prev);

      next.delete(path.id);

      return next;
    });

    try {
      await cartomancerApi.deletePath(
        path.id
      );

      setPaths((ps) =>
        ps.filter((p) => p.id !== path.id)
      );
    } catch (err) {
      // Keep local draft even if delete fails
    }
  };

  const cancelDraftPathAndMaybeRestore =
    async () => {
      if (recalledOriginal) {
        try {
          const restored =
            await cartomancerApi.createPath({
              map_id:
                recalledOriginal.map_id,

              name:
                recalledOriginal.name,

              notes:
                recalledOriginal.notes ||
                "",

              points:
                recalledOriginal.points ||
                [],

              segments:
                recalledOriginal.segments ||
                [],

              legs:
                recalledOriginal.legs ||
                [],

              events: (
                recalledOriginal.events ||
                []
              ).map((e) =>
                e && e.type
                  ? e
                  : null
              ),

              total_distance:
                recalledOriginal.total_distance ||
                0,

              total_duration_days:
                recalledOriginal.total_duration_days ||
                0,

              color:
                recalledOriginal.color ||
                settings?.path_color ||
                "#d4af37",

              ig_day:
                recalledOriginal.ig_day,

              ig_month:
                recalledOriginal.ig_month,

              ig_year:
                recalledOriginal.ig_year,

              ig_hour:
                recalledOriginal.ig_hour,
            });

          setPaths((p) => [
            restored,
            ...p,
          ]);

          setVisiblePathIds((prev) => {
            const next = new Set(prev);

            next.add(restored.id);

            return next;
          });
        } catch (err) {
          alert(
            "Errore ripristino: " +
              err.message
          );
        }
      }

      cancelDraftPath();
    };

  const handleDeleteLog = async (id) => {
    try {
      await cartomancerApi.deleteLog(id);

      setLogs((ls) =>
        ls.filter((l) => l.id !== id)
      );
    } catch (err) {
      alert(err.message);
    }
  };

  const openLocationFromSidebar = (
    loc
  ) => {
    setSelectedLocationId(loc.id);
    setInfoLocation(loc);

    // Trigger zoom to location
    setFocusTarget({
      x: loc.x,
      y: loc.y,
      zoom: 2.5,
      key: `${loc.id}-${Date.now()}`,
    });
  };

  const deleteCurrentMap = async () => {
    if (!map) return;

    try {
      await cartomancerApi.deleteMap(
        map.id
      );

      setMap(null);
      setLocations([]);
      setPaths([]);
      setConfirmDeleteMap(false);
      setInfoLocation(null);
    } catch (err) {
      alert(err.message);
    }
  };

  const dismissTutorialForever =
    async () => {
      try {
        const res =
          await cartomancerApi.updateSettings(
            {
              show_tutorial: false,
            }
          );

        setSettings(res.settings);
      } catch {}

      setShowTutorial(false);
    };

  const isPremium =
    plan === "premium";

  // Cloud sync for Premium users
  const cloudSync = useCloudSync({
    projectId,
    userId:
      user?.user_id || user?.id,
    isPremium,
    logs,
    applyServerLogs: setLogs,
  });

  const showLockedFeature = (
    feature
  ) => setShowPremiumLock(feature);

  if (authLoading || state.loading) {
    return (
      <CartomancerLoading message="Apertura del progetto..." />
    );
  }

  if (state.denied) {
    return (
      <CartomancerAccessDenied
        reason={state.error}
      />
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex flex-col">
        <CartomancerHeader
          title="Progetto"
          backTo="/app"
        />

        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center">
            <p className="font-heading text-ruby mb-4">
              {state.error ||
                "Progetto non trovato"}
            </p>

            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                navigate("/app")
              }
            >
              Torna ai Progetti
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const imageUrl = map?.storage_path
    ? mapFileUrl(map.storage_path)
    : null;

  return (
    <div
      className="h-screen w-screen flex flex-col overflow-hidden"
      data-testid="editor-page"
    >
      <CartomancerHeader
        title={project.name}
        subtitle={
          map
            ? `${
                plan === "premium"
                  ? "PREMIUM"
                  : "BASIC"
              } · ${
                locations.length
              } luoghi · ${
                paths.length
              } percorsi`
            : "Nessuna mappa caricata"
        }
        backTo="/app"
        plan={plan}
        rightSlot={
          <div className="flex items-center gap-2">
            {saveStatus === "saving" && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                Salvataggio...
              </span>
            )}

            {saveStatus === "saved" && (
              <span className="text-xs text-gold flex items-center gap-1">
                <Save className="w-3 h-3" />
                Salvato
              </span>
            )}
          </div>
        }
      />

      {!map ? (
        <UploadPrompt
          onFile={handleUpload}
          uploading={uploading}
          fileInputRef={fileInputRef}
        />
      ) : (
        <div className="flex-1 flex overflow-hidden">
          {/* Left sidebar */}
          <aside
            className="hidden md:flex w-80 border-r border-gold/20 bg-card/60 backdrop-blur flex-col"
            data-testid="editor-sidebar"
          >
            {/* Fantasy Clock */}
            <div className="p-3 border-b border-gold/20">
              <FantasyClock
                style={
                  settings?.clock_style ||
                  "astrolabio"
                }
                scale={
                  settings?.clock_scale ||
                  1
                }
                map={map}
                onShortRest={
                  handleShortRest
                }
                onLongRest={
                  handleLongRest
                }
                onAdvance={advanceDays}
                onEdit={() =>
                  setShowClockEdit(
                    true
                  )
                }
              />
            </div>

            {/* Tools */}
            <div className="p-3 border-b border-gold/20">
              <p className="font-heading text-[10px] tracking-widest text-gold uppercase mb-2">
                Strumenti
              </p>

              <div className="grid grid-cols-4 gap-1">
                <ToolBtn
                  active={tool === "view"}
                  onClick={() =>
                    setTool("view")
                  }
                  icon={
                    <Hand className="w-4 h-4" />
                  }
                  label="Vista"
                  testId="tool-view"
                />

                <ToolBtn
                  active={tool === "pin"}
                  onClick={() =>
                    setTool("pin")
                  }
                  icon={
                    <MapPin className="w-4 h-4" />
                  }
                  label="Pin"
                  testId="tool-pin"
                />

                <ToolBtn
                  active={tool === "path"}
                  onClick={startPath}
                  icon={
                    <Route className="w-4 h-4" />
                  }
                  label="Path"
                  testId="tool-path"
                />

                <ToolBtn
                  active={tool === "ruler"}
                  onClick={() => {
                    setTool("ruler");
                    setRulerPoints([]);
                  }}
                  icon={
                    <Ruler className="w-4 h-4" />
                  }
                  label="Ruler"
                  testId="tool-ruler"
                />
              </div>
            </div>

            {/* Sidebar tabs */}
            <div
              className="border-b border-gold/20 flex"
              data-testid="sidebar-tabs"
            >
              {[
                {
                  id: "places",
                  label: "Luoghi",
                  icon: (
                    <MapPin className="w-3.5 h-3.5" />
                  ),
                },
                {
                  id: "paths",
                  label: "Percorsi",
                  icon: (
                    <Route className="w-3.5 h-3.5" />
                  ),
                },
                {
                  id: "logs",
                  label: "Log",
                  icon: (
                    <History className="w-3.5 h-3.5" />
                  ),
                },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() =>
                    setSidebarTab(t.id)
                  }
                  className={`flex-1 flex items-center justify-center gap-1 py-2 text-[10px] font-heading tracking-widest uppercase transition-all ${
                    sidebarTab === t.id
                      ? "bg-gold/10 text-gold border-b-2 border-gold"
                      : "text-muted-foreground hover:text-gold"
                  }`}
                  data-testid={`sidebar-tab-${t.id}`}
                >
                  {t.icon}
                  {t.label}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto scrollbar-thin">
              {sidebarTab === "places" && (
                <PlacesList
                  locations={locations}
                  onOpen={
                    openLocationFromSidebar
                  }
                  selectedId={
                    selectedLocationId
                  }
                />
              )}

              {sidebarTab === "paths" && (
                <PathsList
                  paths={paths}
                  onDelete={
                    handleDeletePath
                  }
                  onRecall={
                    recallSavedPath
                  }
                  visiblePathIds={
                    visiblePathIds
                  }
                  onToggleVisibility={(
                    id
                  ) => {
                    setVisiblePathIds(
                      (prev) => {
                        const next =
                          new Set(prev);

                        if (
                          next.has(id)
                        ) {
                          next.delete(id);
                        } else {
                          next.add(id);
                        }

                        return next;
                      }
                    );
                  }}
                />
              )}

              {sidebarTab === "logs" && (
                <LogsList
                  logs={logs}
                  onDelete={
                    handleDeleteLog
                  }
                  project={project}
                  unit={map.scale_unit}
                  isPremium={isPremium}
                  showLockedFeature={
                    showLockedFeature
                  }
                  cloudSync={cloudSync}
                />
              )}
            </div>

            {/* Bottom actions */}
            <div className="p-3 border-t border-gold/20 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() =>
                    setShowSettings(true)
                  }
                  className="flex items-center justify-center gap-1 px-2 py-2 rounded border border-gold/40 hover:border-gold hover:bg-gold/10 text-gold font-heading text-[9px] tracking-widest uppercase transition-all"
                  data-testid="sidebar-settings-btn"
                >
                  <Settings className="w-3.5 h-3.5" />
                  Impostazioni
                </button>

                <button
                  onClick={() =>
                    setShowTutorial(true)
                  }
                  className="flex items-center justify-center gap-1 px-2 py-2 rounded border border-arcane/40 hover:border-arcane hover:bg-arcane/10 text-arcane font-heading text-[9px] tracking-widest uppercase transition-all"
                  data-testid="sidebar-tutorial-btn"
                >
                  <Scroll className="w-3.5 h-3.5" />
                  Tutorial
                </button>
              </div>

              <button
                onClick={() =>
                  setConfirmDeleteMap(
                    true
                  )
                }
                className="w-full flex items-center justify-center gap-1 px-2 py-2 rounded border border-border hover:border-ruby/50 hover:text-ruby text-muted-foreground font-heading text-[9px] tracking-widest uppercase transition-all"
                data-testid="sidebar-delete-map-btn"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Elimina Mappa
              </button>

              <div
                className="grid grid-cols-3 gap-1"
                data-testid="premium-features-row"
              >
                <PremiumBadge
                  isPremium={isPremium}
                  icon={
                    <Timer className="w-3.5 h-3.5" />
                  }
                  label="Timeline"
                  onClick={() =>
                    isPremium
                      ? setShowTimeline(true)
                      : showLockedFeature({
                          title:
                            "Timeline della Campagna",
                          description:
                            "Visualizza tutti i viaggi, luoghi visitati e incontri lungo un arco temporale.",
                        })
                  }
                  testId="premium-timeline"
                />

                <PremiumBadge
                  isPremium={isPremium}
                  icon={
                    <BookMarked className="w-3.5 h-3.5" />
                  }
                  label="Diario"
                  onClick={() =>
                    isPremium
                      ? setShowJournal(true)
                      : showLockedFeature({
                          title:
                            "Diario Narrativo",
                          description:
                            "Il sistema trasforma automaticamente i tuoi viaggi ed eventi in pagine di racconto.",
                        })
                  }
                  testId="premium-journal"
                />

                <PremiumBadge
                  isPremium={isPremium}
                  icon={
                    <Scroll className="w-3.5 h-3.5" />
                  }
                  label="Wiki"
                  onClick={() =>
                    isPremium
                      ? setShowWiki(true)
                      : showLockedFeature({
                          title:
                            "Enciclopedia della Campagna",
                          description:
                            "Wiki personale con luoghi, NPC, oggetti, eventi e fazioni, ricercabili.",
                        })
                  }
                  testId="premium-wiki"
                />
              </div>
            </div>
          </aside>

          {/* Canvas + optional right panel */}
          <div className="flex-1 flex relative">
            <div className="flex-1 relative">
              <MapCanvas
                imageUrl={imageUrl}
                tool={tool}
                locations={locations}
                paths={paths}
                draftPath={
                  tool === "path"
                    ? draftPath
                    : null
                }
                rulerPoints={
                  tool === "ruler"
                    ? rulerPoints
                    : []
                }
                scaleInfo={map}
                settings={settings}
                isNight={!!map.is_night}
                selectedLocationId={
                  selectedLocationId
                }
                focusTarget={
                  focusTarget
                }
                hideSavedPaths={
                  tool === "path" &&
                  !!draftPath
                }
                visiblePathIds={
                  visiblePathIds
                }
                onImgSizeChange={
                  setImgSize
                }
                onCanvasClick={
                  handleCanvasClick
                }
                onLocationClick={(loc) => {
                  setSelectedLocationId(
                    loc.id
                  );
                  setInfoLocation(loc);
                }}
                onLocationDrag={
                  handleLocationDrag
                }
                onPathPointDrag={
                  handlePathPointDrag
                }
              />

              {infoLocation && (
                <LocationInfoPanel
                  location={infoLocation}
                  onClose={() => {
                    setInfoLocation(
                      null
                    );
                    setSelectedLocationId(
                      null
                    );
                  }}
                  onEdit={() => {
                    setEditingLoc(
                      infoLocation
                    );
                    setInfoLocation(null);
                  }}
                />
              )}
            </div>

            {/* Path draft panel */}
            {tool === "path" &&
              draftPath && (
                <div className="w-80 shrink-0">
                  <PathManager
                    draftPath={draftPath}
                    legs={legs}
                    travelModes={
                      map.travel_modes ||
                      []
                    }
                    imgSize={imgSize}
                    scaleInfo={map}
                    clipboard={
                      legClipboard
                    }
                    customMods={
                      settings?.custom_modifiers
                    }
                    onLegChange={
                      handleLegChange
                    }
                    onCopyLeg={copyLeg}
                    onPasteLeg={pasteLeg}
                    onDeleteLeg={
                      deleteLeg
                    }
                    onRemoveLastPoint={
                      removeLastPoint
                    }
                    onCancel={
                      cancelDraftPathAndMaybeRestore
                    }
                    onSummary={
                      openSummary
                    }
                  />
                </div>
              )}
          </div>
        </div>
      )}

      {editingLoc && (
        <LocationDialog
          location={editingLoc}
          onClose={() =>
            setEditingLoc(null)
          }
          onSave={handleSaveLocation}
          onDelete={
            handleDeleteLocation
          }
        />
      )}

      {showSummary && map && (
        <TravelSummary
          draftPath={draftPath}
          legs={legs}
          travelModes={
            map.travel_modes || []
          }
          scaleInfo={map}
          imgSize={imgSize}
          map={map}
          customMods={
            settings?.custom_modifiers
          }
          defaultFrom=""
          defaultTo=""
          onClose={() =>
            setShowSummary(false)
          }
          onConfirmTime={(payload) =>
            savePathToDb(
              payload,
              true
            )
          }
          onSavePath={(payload) =>
            savePathToDb(
              payload,
              false
            )
          }
          onDiscard={() => {
            setShowSummary(false);
            cancelDraftPath();
          }}
        />
      )}

      {showSettings && (
        <SettingsPanel
          settings={settings || {}}
          map={map}
          onClose={() =>
            setShowSettings(false)
          }
          onSaveSettings={
            handleSaveSettings
          }
          onSaveMap={handleSaveMap}
        />
      )}

      {showPremiumLock && (
        <PremiumLockPopup
          feature={showPremiumLock}
          onClose={() =>
            setShowPremiumLock(null)
          }
        />
      )}

      {showTutorial && (
        <TutorialOverlay
          onClose={() =>
            setShowTutorial(false)
          }
          onDontShowAgain={
            dismissTutorialForever
          }
        />
      )}

      {showTimeline && (
        <TimelinePanel
          logs={logs}
          locations={locations}
          map={map}
          onClose={() =>
            setShowTimeline(false)
          }
        />
      )}

      {showJournal && (
        <JournalPanel
          logs={logs}
          onClose={() =>
            setShowJournal(false)
          }
        />
      )}

      {showWiki && (
        <WikiPanel
          locations={locations}
          logs={logs}
          onClose={() =>
            setShowWiki(false)
          }
        />
      )}

      {showClockEdit && map && (
        <ClockEditDialog
          map={map}
          onClose={() =>
            setShowClockEdit(false)
          }
          onSave={saveManualDate}
        />
      )}

      {confirmDeleteMap && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/85 backdrop-blur-sm"
          data-testid="delete-map-dialog"
          onMouseDown={(e) =>
            e.target === e.currentTarget &&
            setConfirmDeleteMap(false)
          }
        >
          <div className="rune-border bg-card rounded-lg p-6 max-w-md w-full">
            <h3 className="font-display text-xl text-ruby mb-3">
              Eliminare la mappa?
            </h3>

            <p className="text-sm text-muted-foreground mb-6">
              La mappa e tutti i luoghi e
              percorsi associati verranno
              eliminati. Il progetto e le sue
              impostazioni rimarranno intatti —
              potrai caricare una nuova mappa.
            </p>

            <div className="flex gap-3 justify-end">
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  setConfirmDeleteMap(
                    false
                  )
                }
              >
                Annulla
              </Button>

              <Button
                variant="destructive"
                size="sm"
                onClick={deleteCurrentMap}
                data-testid="confirm-delete-map"
              >
                Elimina Mappa
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Small helpers ---------- */

function ToolBtn({
  active,
  onClick,
  icon,
  label,
  testId,
}) {
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center gap-1 px-2 py-2 rounded transition-all ${
        active
          ? "bg-gold/20 text-gold border border-gold"
          : "border border-transparent hover:bg-gold/10 text-muted-foreground"
      }`}
      data-testid={testId}
    >
      {icon}

      {label && (
        <span className="text-[9px] font-heading tracking-wider uppercase">
          {label}
        </span>
      )}
    </button>
  );
}

function PremiumBadge({
  isPremium,
  icon,
  label,
  onClick,
  testId,
}) {
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center gap-1 px-1 py-2 rounded border transition-all relative ${
        isPremium
          ? "border-arcane/40 bg-arcane/10 text-arcane hover:bg-arcane/20"
          : "border-border/50 text-muted-foreground hover:border-arcane/40 hover:text-arcane"
      }`}
      data-testid={testId}
    >
      {icon}

      <span className="text-[8px] font-heading tracking-wider uppercase">
        {label}
      </span>

      {!isPremium && (
        <Lock className="absolute top-0.5 right-0.5 w-2.5 h-2.5" />
      )}
    </button>
  );
}

function PlacesList({
  locations,
  onOpen,
  selectedId,
}) {
  return (
    <div
      className="p-2 space-y-0.5"
      data-testid="locations-list"
    >
      {locations.length === 0 && (
        <p className="text-xs text-muted-foreground italic px-2 py-4 text-center">
          Nessun luogo. Seleziona "Pin" e
          clicca sulla mappa.
        </p>
      )}

      {locations.map((l) => (
        <button
          key={l.id}
          onClick={() => onOpen(l)}
          className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded transition-all ${
            selectedId === l.id
              ? "bg-gold/20 text-gold"
              : "hover:bg-gold/10 text-parchment"
          }`}
          data-testid={`loc-item-${l.id}`}
        >
          <CategoryIcon
            id={l.category}
            className="w-4 h-4 shrink-0"
          />

          <span className="text-xs truncate flex-1">
            {l.name}
          </span>
        </button>
      ))}
    </div>
  );
}

function PathsList({
  paths,
  onDelete,
  onRecall,
  visiblePathIds,
  onToggleVisibility,
}) {
  return (
    <div
      className="p-2 space-y-1"
      data-testid="paths-list"
    >
      {paths.length === 0 && (
        <p className="text-xs text-muted-foreground italic px-2 py-4 text-center">
          Nessun percorso salvato.
        </p>
      )}

      {paths.map((p) => {
        const isVisible =
          visiblePathIds?.has(p.id);

        return (
          <div
            key={p.id}
            className="rune-border rounded bg-card/50 p-2 group"
            data-testid={`path-item-${p.id}`}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0 flex-1">
                <p className="font-heading text-xs text-gold truncate">
                  {p.name}
                </p>

                <p className="text-[10px] text-muted-foreground">
                  {p.points?.length || 0} pts ·{" "}
                  {formatDistance(
                    p.total_distance || 0,
                    p.unit || ""
                  )}{" "}
                  ·{" "}
                  {formatDurationDays(
                    p.total_duration_days ||
                      0
                  )}
                </p>
              </div>

              <button
                onClick={() =>
                  onToggleVisibility?.(
                    p.id
                  )
                }
                className= {`p-1 rounded border border-border hover:border-ruby/50 hover:text-ruby text-muted-foreground transition-all ${
                  isVisible
                    ? "text-gold"
                    : "text-muted-foreground hover:text-gold"
                }`}
                title={
                  isVisible
                    ? "Nascondi dalla mappa"
                    : "Mostra sulla mappa"
                }
                data-testid={`path-toggle-${p.id}`}
              >
                {isVisible ? (
                  <Eye className="w-3.5 h-3.5" />
                ) : (
                  <EyeOff className="w-3.5 h-3.5" />
                )}
              </button>

              <button
                onClick={() =>
                  onDelete(p.id)
                }
                className="p-1 rounded border border-border hover:border-ruby/50 hover:text-ruby text-muted-foreground transition-all"
                title="Elimina percorso"
                data-testid={`path-delete-${p.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>

            </div>
          </div>
        );
      })}
    </div>
  );
}

function LogsList({
  logs,
  onDelete,
  project,
  unit,
  isPremium,
  showLockedFeature,
  cloudSync,
}) {
  const exportTxt = (log) => {
    const lines = [];

    lines.push(
      `=== ${log.title} ===`
    );

    lines.push(
      `Data: ${log.date_str}`
    );

    lines.push(
      `Distanza: ${formatDistance(
        log.distance || 0,
        log.unit || unit || ""
      )}`
    );

    lines.push(
      `Durata: ${formatDurationDays(
        log.duration_days || 0
      )}`
    );

    if (log.transports?.length) {
      lines.push(
        `Mezzi: ${log.transports.join(
          ", "
        )}`
      );
    }

    if (log.events?.length) {
      lines.push("");
      lines.push("Eventi:");

      log.events.forEach((e, i) => {
        lines.push(
          `  ${i + 1}. ${e.type}${
            e.description
              ? " - " +
                e.description
              : ""
          }`
        );
      });
    }

    if (log.notes) {
      lines.push("");
      lines.push("Note:");
      lines.push(log.notes);
    }

    const blob = new Blob(
      [lines.join("\n")],
      {
        type: "text/plain",
      }
    );

    const url =
      URL.createObjectURL(blob);

    const a =
      document.createElement("a");

    a.href = url;

    a.download = `${log.title.replace(
      /[^a-z0-9]+/gi,
      "_"
    )}.txt`;

    a.click();

    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="p-2 space-y-1"
      data-testid="logs-list"
    >
      {isPremium && cloudSync && (
        <CloudSyncBadge
          state={cloudSync.state}
          lastSyncedAt={
            cloudSync.lastSyncedAt
          }
          driftInfo={
            cloudSync.driftInfo
          }
          errorMsg={
            cloudSync.errorMsg
          }
          onSyncNow={
            cloudSync.syncNow
          }
          onAcknowledgeConflict={
            cloudSync.acknowledgeConflict
          }
        />
      )}

      {logs.length === 0 ? (
        <p className="text-xs text-muted-foreground italic px-2 py-4 text-center">
          Nessun log di viaggio. Conferma
          uno spostamento per crearne uno.
        </p>
      ) : (
        logs.map((log) => (
          <div
            key={log.id}
            className="rune-border rounded bg-card/50 p-2 group"
            data-testid={`log-item-${log.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="font-heading text-xs text-gold truncate">
                  {log.title}
                </p>

                <p className="text-[10px] text-muted-foreground">
                  {log.date_str}
                </p>

                <p className="text-[10px] text-parchment">
                  {formatDistance(
                    log.distance || 0,
                    log.unit || unit || ""
                  )}{" "}
                  ·{" "}
                  {formatDurationDays(
                    log.duration_days ||
                      0
                  )}
                </p>
              </div>

              <div className="flex flex-col gap-1">
                <button
                  onClick={() =>
                    exportTxt(log)
                  }
                  className="text-muted-foreground hover:text-gold"
                  title="Esporta TXT"
                  data-testid={`log-export-${log.id}`}
                >
                  <Download className="w-3.5 h-3.5" />
                </button>

                <button
                  onClick={() =>
                    onDelete(log.id)
                  }
                  className="text-muted-foreground hover:text-ruby"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))
      )}

      {/* Locked teaser for Free */}
      {!isPremium && (
        <button
          onClick={() =>
            showLockedFeature({
              title:
                "Sincronizzazione Cloud dei Log",
              description:
                "Salva e sincronizza i tuoi log di viaggio su cloud, accessibili da ogni dispositivo.",
            })
          }
          className="w-full mt-3 flex items-center justify-center gap-1.5 px-2 py-2 rounded border font-heading text-[9px] tracking-widest uppercase transition-all border-border text-muted-foreground hover:border-arcane hover:text-arcane"
          data-testid="cloud-sync-teaser"
        >
          <Lock className="w-3 h-3" />
          <Sparkles className="w-3 h-3" />
          Sync Cloud Premium
        </button>
      )}
    </div>
  );
}

function UploadPrompt({
  onFile,
  uploading,
  fileInputRef,
}) {
  const [dragging, setDragging] =
    useState(false);

  return (
    <div
      className="flex-1 flex items-center justify-center p-6"
      data-testid="upload-prompt"
    >
      <div
        className={`max-w-xl w-full rune-border rounded-2xl p-10 backdrop-blur transition-all ${
          dragging
            ? "bg-gold/10 gold-glow"
            : "bg-card/70"
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() =>
          setDragging(false)
        }
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);

          if (
            e.dataTransfer.files?.[0]
          ) {
            onFile(
              e.dataTransfer.files[0]
            );
          }
        }}
      >
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-6">
            <Upload className="w-10 h-10 text-gold" />
          </div>

          <h2 className="font-display text-3xl text-gold mb-3">
            Carica la Tua Mappa
          </h2>

          <p className="font-serif-fantasy italic text-muted-foreground mb-8">
            « Trascina un'immagine oppure
            sfoglia i tuoi file »
          </p>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) =>
              e.target.files?.[0] &&
              onFile(
                e.target.files[0]
              )
            }
            data-testid="map-file-input"
          />

          <Button
            variant="primary"
            size="lg"
            onClick={() =>
              fileInputRef.current?.click()
            }
            disabled={uploading}
            data-testid="upload-map-btn"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Caricamento...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Scegli Immagine
              </>
            )}
          </Button>

          <p className="text-xs text-muted-foreground mt-6">
            Formati supportati: PNG, JPG,
            WEBP · Max 15MB
          </p>
        </div>
      </div>
    </div>
  );
}

function ClockEditDialog({
  map,
  onClose,
  onSave,
}) {
  const [day, setDay] = useState(
    map.ig_day || 1
  );

  const [month, setMonth] = useState(
    map.ig_month || 1
  );

  const [year, setYear] = useState(
    map.ig_year || 1
  );

  const [hourDec, setHourDec] =
    useState(map.ig_hour || 12);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener(
      "keydown",
      onKey
    );

    return () => {
      window.removeEventListener(
        "keydown",
        onKey
      );
    };
  }, [onClose]);

  const h = Math.floor(hourDec);

  const m = Math.round(
    (hourDec - h) * 60
  );

  const setHM = (nh, nm) => {
    const nhClamped = Math.max(
      0,
      Math.min(23, nh)
    );

    const nmClamped = Math.max(
      0,
      Math.min(59, nm)
    );

    setHourDec(
      nhClamped + nmClamped / 60
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/85 backdrop-blur-sm"
      onMouseDown={(e) =>
        e.target === e.currentTarget &&
        onClose()
      }
      data-testid="clock-edit-dialog"
    >
      <div className="rune-border bg-card rounded-lg max-w-md w-full overflow-hidden">
        <div className="p-5 border-b border-border flex items-start justify-between">
          <div>
            <h3 className="font-display text-xl text-gold">
              Modifica Data e Ora
            </h3>

            <p className="font-serif-fantasy italic text-sm text-muted-foreground">
              « Riscrivi il tempo della tua
              campagna »
            </p>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 rounded-md border border-gold/30 text-muted-foreground hover:text-gold hover:border-gold hover:bg-gold/10 active:bg-gold/20 transition-all"
            data-testid="clock-edit-close"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block font-heading text-[10px] tracking-widest text-parchment uppercase mb-1">
                Giorno
              </label>

              <input
                type="number"
                min="1"
                max="30"
                value={day}
                onChange={(e) =>
                  setDay(
                    Math.max(
                      1,
                      Math.min(
                        30,
                        parseInt(
                          e.target.value
                        ) || 1
                      )
                    )
                  )
                }
                className="w-full bg-input border border-gold/30 rounded px-3 py-2 text-sm"
                data-testid="clock-edit-day"
              />
            </div>

            <div>
              <label className="block font-heading text-[10px] tracking-widest text-parchment uppercase mb-1">
                Mese
              </label>

              <select
                value={month}
                onChange={(e) =>
                  setMonth(
                    parseInt(
                      e.target.value
                    )
                  )
                }
                className="w-full bg-input border border-gold/30 rounded px-3 py-2 text-sm"
                data-testid="clock-edit-month"
              >
                {(map.months || []).map(
                  (mm, i) => (
                    <option
                      key={i}
                      value={i + 1}
                    >
                      {mm}
                    </option>
                  )
                )}
              </select>
            </div>

            <div>
              <label className="block font-heading text-[10px] tracking-widest text-parchment uppercase mb-1">
                Anno
              </label>

              <input
                type="number"
                min="1"
                value={year}
                onChange={(e) =>
                  setYear(
                    Math.max(
                      1,
                      parseInt(
                        e.target.value
                      ) || 1
                    )
                  )
                }
                className="w-full bg-input border border-gold/30 rounded px-3 py-2 text-sm"
                data-testid="clock-edit-year"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block font-heading text-[10px] tracking-widest text-parchment uppercase mb-1">
                Ora
              </label>

              <input
                type="number"
                min="0"
                max="23"
                value={h}
                onChange={(e) =>
                  setHM(
                    parseInt(
                      e.target.value
                    ) || 0,
                    m
                  )
                }
                className="w-full bg-input border border-gold/30 rounded px-3 py-2 text-sm"
                data-testid="clock-edit-hour"
              />
            </div>

            <div>
              <label className="block font-heading text-[10px] tracking-widest text-parchment uppercase mb-1">
                Minuti
              </label>

              <input
                type="number"
                min="0"
                max="59"
                value={m}
                onChange={(e) =>
                  setHM(
                    h,
                    parseInt(
                      e.target.value
                    ) || 0
                  )
                }
                className="w-full bg-input border border-gold/30 rounded px-3 py-2 text-sm"
                data-testid="clock-edit-minute"
              />
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-border flex justify-end gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
          >
            Annulla
          </Button>

          <Button
            variant="primary"
            size="sm"
            onClick={() =>
              onSave({
                ig_day: day,
                ig_month: month,
                ig_year: year,
                ig_hour: hourDec,
              })
            }
            data-testid="clock-edit-save"
          >
            Salva
          </Button>
        </div>
      </div>
    </div>
  );
}